import { Divider, InfoPopover, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { CapsuleTrend } from '@/components';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { ForestIntelligenceExplainer } from '@/components/NCExplainers/ForestIntelligenceExplainer';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import {
  BENCHMARK_FACT_TYPE,
  BENCHMARK_FACT_VALUE_TYPE,
  BENCHMARK_TO_BENCHMARK_TYPE,
  FACT_TYPE,
  LATEST_FACT_TYPES,
} from '@/components/widgets/config/useForestIntelligenceChart';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { COMPLIANCE_LOGOS_DEFORESTATION } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';
import { getStyleForBenchmark } from '@/utils/getStyleForBenchmark';

export const ForestIntelligence = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.forestIntelligence.explainers.forestIntelligence.title')}>
      <ForestIntelligenceCard />
    </PlotCardWrapper>
  );
};

const ForestIntelligenceCard = () => {
  const { t } = useTranslation();

  const factNames = [
    IndicatorName.forest_intelligence_sla_percentile,
    IndicatorName.forest_intelligence_sla_comparison,
    IndicatorName.forest_intelligence_lnc_percentile,
    IndicatorName.forest_intelligence_lnc_comparison,
    IndicatorName.forest_intelligence_lpc_percentile,
    IndicatorName.forest_intelligence_lpc_comparison,
    IndicatorName.forest_intelligence_functional_diversity_percentile,
    IndicatorName.forest_intelligence_functional_diversity_comparison,
  ];
  const forestFacts = usePlotFacts(factNames);
  const ecoregionFacts = usePlotFacts([IndicatorName.forest_intelligence_ecoregion_id], undefined, {
    [PlotReportFactLabelNameEnum.class_name]: '*',
  });

  if (isNullish(forestFacts) || isNullish(ecoregionFacts)) {
    return null;
  }

  const facts = [...forestFacts, ...ecoregionFacts];

  return (
    <NCCard explainer={<ForestIntelligenceExplainer />} data-testid='forest-intelligence-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.forestIntelligence.explainers.forestIntelligence.title')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <ForestIntelligenceGraphTile facts={facts} />
        <ForestIntelligenceStatsTile facts={facts} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_DEFORESTATION} />
    </NCCard>
  );
};

const ForestIntelligenceGraphTile = ({ facts }: { facts?: PlotReportFactElement[] }) => {
  const widgetConfiguration = {
    name: 'forest_intelligence',
    type: WidgetType.o_c_forest_intelligence_chart,
    visible: true,
    relatedData: {
      facts,
      styles: {
        root: 'rounded-none border-none px-0',
        header: 'pl-0',
        body: 'px-0',
      },
    },
  };
  return <WidgetSlotRenderer nestedWidget={widgetConfiguration} />;
};

const ForestIntelligenceStatsTile = ({ facts }: { facts?: PlotReportFactElement[] }) => {
  const { t } = useTranslation();

  const statsData = Object.keys(LATEST_FACT_TYPES).flatMap((factType) => {
    const fact = getFactFromFactArray(facts, factType);
    const factValue = fact?.value;
    if (isNullish(factValue) || typeof factValue !== 'number') return [];

    const benchmarkFactType = LATEST_FACT_TYPES[factType as keyof typeof LATEST_FACT_TYPES];
    const benchmarkValue = Math.round(getFactFromFactArray(facts, benchmarkFactType)?.value as number);

    const benchmarkType = BENCHMARK_TO_BENCHMARK_TYPE[String(benchmarkValue ?? 0) as BENCHMARK_FACT_VALUE_TYPE];
    const benchmarkColor = getStyleForBenchmark(benchmarkType).primary;

    return {
      latestFact: fact,
      factBenchmark: { name: benchmarkFactType, value: benchmarkValue, color: benchmarkColor },
    };
  });

  return (
    <Stack className='flex flex-col gap-10 justify-center items-center' data-testid='forest-intelligence-stats-tile'>
      {statsData.map(({ latestFact, factBenchmark }) => (
        <NCFactCapsule
          key={latestFact?.name}
          label={t(`shared.ncaDetail.details.forestIntelligence.labels.components.${latestFact?.name as FACT_TYPE}`)}
          fact={latestFact}
          capsuleIconProps={{
            icon: getNCChangeIcon(latestFact),
            variant: getNCChangeIconVariant(latestFact),
            style: { color: factBenchmark.color },
          }}
          trend={
            typeof latestFact?.value === 'number' && (
              <CapsuleTrend
                variant='negative'
                className='flex flex-row gap-1 typography-caption font-normal text-text-secondary'
              >
                {t(
                  `shared.ncaDetail.details.forestIntelligence.labels.component_benchmarks.${factBenchmark.name as BENCHMARK_FACT_TYPE}.${String(factBenchmark.value) as BENCHMARK_FACT_VALUE_TYPE}`,
                )}
                <InfoPopover
                  position='right'
                  {...{
                    title: t(
                      `shared.ncaDetail.details.forestIntelligence.labels.component_benchmarks.${factBenchmark.name as BENCHMARK_FACT_TYPE}.${String(factBenchmark.value) as BENCHMARK_FACT_VALUE_TYPE}`,
                    ),
                    body: t(
                      `shared.ncaDetail.details.forestIntelligence.explainers.component_benchmarks.${factBenchmark.name as BENCHMARK_FACT_TYPE}.${String(factBenchmark.value) as BENCHMARK_FACT_VALUE_TYPE}`,
                    ),
                  }}
                />
              </CapsuleTrend>
            )
          }
        />
      ))}
    </Stack>
  );
};
